import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  Injector,
  viewChild,
} from '@angular/core';
import { onBooleanTransition } from '../utils/signal-transition.util';
import { focusIfOrphaned } from '../utils/focus.util';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { filter, map } from 'rxjs/operators';

import { MatIcon, MatIconModule } from '@angular/material/icon';
import { NavigationEnd, Router } from '@angular/router';
import { NotificationsComponent } from '../../core/layout/common/notifications/notifications.component';
import { UserComponent } from '../../core/layout/common/user/user.component';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UserPreferenceStore } from '../../core/services/user-preference/user-preference.store';
import { WidgetLayoutEditService } from '../services/widget-layout-edit.service';
import { HideForPublicPageDirective } from '../../core/auth/directives/hide-for-public-page.directive';
import { HideForOnboardingDirective } from '../../core/auth/directives/hide-for-onboarding.directive';
import { CanViewModuleDirective } from '../../core/auth/directives/can-view-module.directive';
import { ActionWatcherComponent } from '../../features/admin/modules/features/actions/ui/action-watcher.component';
import { MatDialog } from '@angular/material/dialog';
import { ShowForOriginalAdminDirective } from '../../core/auth/directives/show-for-original-admin.directive';
import { ImpersonationSwitcherComponent } from '../../core/layout/common/impersonation-switcher/impersonation-switcher.component';
import { ModulesStore } from '../../features/admin/modules/modules.store';
import { FeedbackTriggerButtonComponent } from './feedback-trigger-button.component';
import { SignalRStatusComponent } from './signalr-status.component';

@Component({
  selector: 'app-icon-menu-items',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <button
      mat-icon-button
      (click)="openActionWatcher()"
      matTooltip="Action watcher"
      *showForOriginalAdmin
      data-cy="action-watcher-button">
      <mat-icon svgIcon="feather:activity"></mat-icon>
    </button>
    <app-impersonation-switcher *showForOriginalAdmin></app-impersonation-switcher>
    <div *showForOriginalAdmin class="bg-accent w-0.5 h-6 mt-2"></div>

    <!-- Deliberately NOT behind *hideForPublicPage / *hideForOnboarding: the floating button this
         replaced (#20919) was available on every surface, and gating it here would take feedback
         away from exactly the users most likely to be stuck. -->
    <!-- Renders nothing while the realtime connection is healthy; see the component. -->
    <app-signalr-status></app-signalr-status>
    <app-feedback-trigger-button></app-feedback-trigger-button>
    <user></user>
    <notifications
      *canViewModule="{ moduleId: getNotificationsModuleId(), show: true }"
      data-cy="notifications-button"></notifications>
    <ng-container *hideForOnboarding>
      @if (isModuleRoute()) {
        <!-- Every sibling in this toolbar carries an
             aria-label + tooltip pair (action-watcher-button's matTooltip, notifications-toggle's
             aria-label + aria-expanded, feedback-trigger-button's aria + matTooltip) — this button
             had none, so a screen-reader user's only description of the sole entry point to the
             quick-view drawer was "button". matTooltip (not tippyTooltip): matches the DIRECT
             sibling in this exact toolbar (action-watcher-button), the convention this specific
             component already follows, over the app-wide tippy-for-simple-tooltips default.
             aria-expanded mirrors notifications-toggle's own pattern one button over. The active
             fill (widgets-sidebar-button--active, styles below) gives a SIGHTED user the same
             "currently open" signal the aria-expanded gives an AT user — every other
             header/toolbar button in this feature an aria+tooltip pair, this toggle was the one
             left out.

             Disabled while a widget layout-edit session runs (WidgetLayoutEditService): the
             drawer and the edit toolbar both claim the viewport's right edge, and
             WidgetFlowAreaComponent.startEditing already closes an open drawer on entry — this
             button is the one way to reopen it mid-session, so it is taken away rather than
             allowed to stack the two (user decision 2026-08-25).

             disabledInteractive, the same shape bulk-edit-bar.component.ts's save button uses:
             a plain disabled button takes no focus and fires no hover events, so the tooltip —
             which swaps to the disabled REASON below — would vanish for everyone, and keyboard/AT
             users would never learn why the icon is grey. This keeps the button focusable and
             hoverable (aria-disabled, not native disabled) with the tooltip and its
             aria-describedby on the button itself. The trade-off: Material only intercepts
             clicks for disabled-interactive ANCHORS, never buttons — so [disabled] below is
             purely a visual + AT signal, and the early return in toggleDrower IS the
             enforcement. Removing that gate re-arms this button mid-edit-session. -->
        <button
          #widgetsSidebarButton
          mat-icon-button
          (click)="toggleDrower()"
          *hideForPublicPage
          [matTooltip]="
            layoutEditActive() ? 'Niet beschikbaar tijdens het bewerken van de indeling' : 'Widgets snel bekijken'
          "
          aria-label="Widgets snel bekijken"
          [attr.aria-expanded]="widgetSidebarOpen()"
          [class.widgets-sidebar-button--active]="widgetSidebarOpen()"
          [disabled]="layoutEditActive()"
          [disabledInteractive]="true"
          data-cy="widgets-sidebar-button">
          <mat-icon svgIcon="mat_outline:widgets"></mat-icon>
        </button>
      }
    </ng-container>
  `,
  imports: [
    MatIcon,
    MatButtonModule,
    MatTooltipModule,
    MatIconModule,
    NotificationsComponent,
    UserComponent,
    HideForPublicPageDirective,
    HideForOnboardingDirective,
    CanViewModuleDirective,
    ShowForOriginalAdminDirective,
    ImpersonationSwitcherComponent,
    FeedbackTriggerButtonComponent,
    SignalRStatusComponent,
  ],
  styles: `
    /*
     * a subtle, always-on state-layer fill while the
     * drawer is open, so a sighted user has a visible cue too — the same hover state-layer colour
     * (--fuse-bg-hover) every other icon-button in this toolbar family already uses, just applied
     * without needing :hover.
     */
    .widgets-sidebar-button--active.mat-mdc-icon-button {
      background-color: var(--fuse-bg-hover);
    }
  `,
})
export class IconMenuItemsComponent {
  dialog = inject(MatDialog);
  private readonly userPreferenceStore = inject(UserPreferenceStore);
  /** Disables the drawer toggle while any widget layout-edit session runs — see the template
   *  comment on the button. */
  protected readonly layoutEditActive = inject(WidgetLayoutEditService).anyEditing;
  private readonly modulesStore = inject(ModulesStore);
  private readonly router = inject(Router);

  /**
   * Current URL, mirrored into a signal off `NavigationEnd` (same reuse as
   * `LayoutComponent.#currentUrl`, layout.component.ts:125) — `Router.url` itself is not reactive.
   */
  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(() => this.router.url),
      takeUntilDestroyed()
    ),
    { initialValue: this.router.url }
  );

  /**
   * Whether the current route is a module page mounted under `ModulesComponent`
   * (modules.routes.ts / modules.component.ts) — the only place `<app-widget-sidebar>` (and with
   * it the quick-view drawer) is mounted. Non-module routes such as
   * `/beheerders-instellingen/**` or `/demo/**` still render this toolbar, but pressing the
   * button there flips `ui.widgetSidebar` with no drawer on screen to show for it (carried from
   * an early review of this component).
   *
   * Detection reuses `ModulesStore.getModuleBySlug` on the URL's first path segment — the same
   * slug lookup `NavigationService.currentPageIsPublic` (navigation.service.ts:46) and the route
   * guards (auth.guard.ts, entry-exists.guard.ts, public-page.ts) already use to resolve "which
   * module does this URL belong to". A module route (including `/dashboard`, itself an ordinary
   * page module) resolves to a module here and shows the button; an admin/demo/utility route does
   * not and hides it.
   */
  protected readonly isModuleRoute = computed(() => {
    const slug = this.currentUrl().split('?')[0].split('#')[0].split('/').filter(Boolean)[0];
    return !!slug && !!this.modulesStore.getModuleBySlug(decodeURIComponent(slug));
  });

  // Same `read: ElementRef` requirement as every other viewChild ref on a mat-*-button in
  // this codebase (a bare ref resolves to the MatButton component, not the DOM node — see
  // widget-overlay-drawer.component.ts's drawerHostRef for the fuse-drawer equivalent).
  private readonly widgetsSidebarButtonRef = viewChild<ElementRef<HTMLButtonElement>, ElementRef<HTMLButtonElement>>(
    'widgetsSidebarButton',
    { read: ElementRef }
  );
  readonly #injector = inject(Injector);

  /** Same preference this button itself writes (`toggleDrower` below). Originally read here only
   *  to detect the CLOSE transition for focus-return (below); it
   *  also drives the button's own `aria-expanded` and active-state styling
   *  — `widgetSidebarOpen` (protected) is the SAME signal, aliased so the template can read it
   *  (a true `#`-private field is not reachable from a component's own template). */
  readonly #widgetSidebarOpen = computed(
    () => this.userPreferenceStore.userPreference()?.ui?.widgetSidebar === 'open'
  );
  protected readonly widgetSidebarOpen = this.#widgetSidebarOpen;

  constructor() {
    // The quick-view drawer's own close paths (its X button, and Escape via the shared
    // shell) leave focus on `<body>` once the panel unmounts, because nothing returns it to the
    // button that opened it — and that button lives HERE, not on the drawer or its shell
    // (WidgetSidebarComponent), which is why this fix could not live there. Mirrors the dashboard
    // manage-panel's already-working `onCloseManage` (widget-flow-area.component.ts,
    // `afterNextRender(() => manageButtonRef()?.nativeElement.focus())`), but reached reactively
    // off the shared preference signal instead of a local close handler, since this component has
    // no close handler of its own to hook — it only ever flips `toggleDrower`, and the actual close
    // can happen from inside the drawer with no call back into this component at all.
    //
    // 'fall' = only a genuine open->closed transition, never the first run: a drawer that is
    // already closed when this component boots must not steal focus onto a button the user never
    // interacted with.
    onBooleanTransition(this.#widgetSidebarOpen, 'fall', () =>
      afterNextRender(() => this.#returnFocusIfOrphaned(), { injector: this.#injector })
    );
  }

  /**
   * Returns focus to the drawer toggle — but ONLY when the closing drawer actually orphaned it.
   *
   * The trigger is a preference transition, not a close handler, so it fires for every open→closed
   * write of `ui.widgetSidebar`, including ones the user did not aim at this button:
   * `WidgetFlowAreaComponent.openManage()` releases the right edge that way before opening the
   * manage panel, and a user-preference reload/rollback can flip the same value while the user is
   * typing somewhere else entirely. Focusing unconditionally yanked the caret out of whatever they
   * were doing.
   *
   * `document.body` (or nothing) as `activeElement` is exactly the state a panel leaves behind when
   * it unmounts with focus inside it — the case this exists for. Any other active element means
   * something still owns focus and this button is not entitled to take it: a click on "Widgets
   * beheren" leaves that button focused, so the manage-panel path now skips this entirely instead
   * of bouncing focus through the toolbar on its way into the panel.
   */
  #returnFocusIfOrphaned(): void {
    // Session gate, not just the orphan check: with disabledInteractive the button is genuinely
    // focusable while aria-disabled, so without this line the reclaim COULD land focus on the
    // greyed-out toggle mid-edit-session (the native-disabled focus() no-op that used to make
    // that impossible is gone by design). The flow-area's own park owns focus during a session.
    if (this.layoutEditActive()) return;
    focusIfOrphaned(this.widgetsSidebarButtonRef()?.nativeElement);
  }

  toggleDrower(): void {
    // The click gate for the disabled-interactive state: Material suppresses clicks on
    // disabled-interactive ANCHORS only (verified: `_setupAsAnchor` is the sole interception in
    // _icon-button-chunk.mjs), so a native button's (click) still fires while aria-disabled.
    // Without this line the disabled toggle would happily reopen the drawer mid-edit-session.
    if (this.layoutEditActive()) return;
    this.userPreferenceStore.editUI({
      widgetSidebar: this.userPreferenceStore.userPreference()?.ui.widgetSidebar === 'open' ? 'closed' : 'open',
    });
  }

  openActionWatcher(): void {
    this.dialog.open(ActionWatcherComponent, {
      width: '1280px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      data: {
        moduleId: null,
      },
    });
  }

  getNotificationsModuleId(): string | undefined {
    return this.modulesStore.moduleCodeToId('notifications');
  }
}
